import React, { useState, useEffect, useCallback } from "react";
import { Table, Card, Tag, Input, Space, Button, message, Modal, Form, Select, Switch, Popconfirm } from "antd";
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, LayoutOutlined } from "@ant-design/icons";
import { dashboardService, userManagementService } from "../../../api/enhancedApi";
import { formatDate } from "../../../utils/dateUtils";
import PermissionGuard from "../../../components/auth/PermissionGuard";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

const DashboardTemplateList = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [form] = Form.useForm();

    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const data = await dashboardService.getTemplates();
            setTemplates(data);
        } catch (error) {
            console.error("Failed to fetch templates:", error);
            message.error("获取模版列表失败");
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchRoles = useCallback(async () => {
        try {
            const data = await userManagementService.fetchRoles({ page: 1, page_size: 100 });
            setRoles(data.items);
        } catch (error) {
            console.error("Failed to fetch roles:", error);
        }
    }, []);

    useEffect(() => {
        fetchTemplates();
        fetchRoles();
    }, [fetchTemplates, fetchRoles]);

    const handleCreate = () => {
        setEditingTemplate(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingTemplate(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await dashboardService.deleteTemplate(id);
            message.success("模版删除成功");
            fetchTemplates();
        } catch (error) {
            console.error("Failed to delete template:", error);
            message.error("删除失败");
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();

            // 如果是新建,需要提供一个默认布局(空数组或系统默认)
            // 这里我们先给一个空数组,实际编辑布局时会填充
            if (!editingTemplate) {
                values.layout_data = []; // 使用 layout_data 字段名,与后端一致
            }

            if (editingTemplate) {
                await dashboardService.updateTemplate(editingTemplate.id, values);
                message.success("模版更新成功");
            } else {
                await dashboardService.createTemplate(values);
                message.success("模版创建成功");
            }
            setIsModalVisible(false);
            fetchTemplates();
        } catch (error) {
            console.error("Failed to save template:", error);
            message.error("保存失败");
        }
    };

    const handleDesignLayout = (record) => {
        navigate(`/templates/${record.id}/design`);
    };

    const filteredTemplates = templates.filter((t) => t.name.toLowerCase().includes(searchText.toLowerCase()) || (t.description && t.description.toLowerCase().includes(searchText.toLowerCase())));

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 80,
        },
        {
            title: "模版名称",
            dataIndex: "name",
            key: "name",
            render: (text, record) => (
                <Space>
                    <span style={{ fontWeight: 500 }}>{text}</span>
                    {record.is_default && <Tag color="green">默认</Tag>}
                </Space>
            ),
        },
        {
            title: "关联角色",
            dataIndex: "role_id",
            key: "role_id",
            render: (roleId) => {
                const role = roles.find((r) => r.id === roleId);
                return role ? <Tag color="blue">{role.name}</Tag> : <span style={{ color: "#999" }}>未关联</span>;
            },
        },
        {
            title: "描述",
            dataIndex: "description",
            key: "description",
            ellipsis: true,
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 180,
            render: (date) => formatDate(date),
        },
        {
            title: "操作",
            key: "action",
            width: 250,
            render: (_, record) => (
                <Space size="middle">
                    <PermissionGuard permission="dashboard_template:update">
                        <Button type="link" icon={<LayoutOutlined />} onClick={() => handleDesignLayout(record)}>
                            设计布局
                        </Button>
                        <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    </PermissionGuard>
                    <PermissionGuard permission="dashboard_template:delete">
                        <Popconfirm title="确定要删除这个模版吗?" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <div className="page-header-compact">
                <div className="page-title-compact">Dashboard 模版管理</div>
                <div className="page-action-compact">
                    <Space>
                        <Input placeholder="搜索模版" prefix={<SearchOutlined />} value={searchText} onChange={(e) => setSearchText(e.target.value)} style={{ width: 250 }} allowClear />
                        <PermissionGuard permission="dashboard_template:create">
                            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                                新建模版
                            </Button>
                        </PermissionGuard>
                    </Space>
                </div>
            </div>

            <Card className="data-card">
                <Table
                    columns={columns}
                    dataSource={filteredTemplates}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        defaultPageSize: 20,
                        showSizeChanger: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    size="small"
                />
            </Card>

            <Modal title={editingTemplate ? "编辑模版" : "新建模版"} open={isModalVisible} onOk={handleModalOk} onCancel={() => setIsModalVisible(false)} destroyOnClose>
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="模版名称" rules={[{ required: true, message: "请输入模版名称" }]}>
                        <Input placeholder="例如：航路管理员默认看板" />
                    </Form.Item>
                    <Form.Item name="description" label="描述">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="role_id" label="关联角色">
                        <Select placeholder="选择关联角色" allowClear>
                            {roles.map((role) => (
                                <Option key={role.id} value={role.id}>
                                    {role.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="is_default" label="设为角色默认" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default DashboardTemplateList;
